import { deleteToken, getMessaging, getToken, isSupported, onMessage, type MessagePayload } from "firebase/messaging";
import { httpsCallable } from "firebase/functions";
import { auth, firebaseApp, functions } from "./firebase";

export type PushState = "unsupported" | "blocked" | "off" | "on";
const registerToken = httpsCallable<{ token: string; deviceName: string }, { registered: boolean; rotate?: boolean; reason?: string }>(functions, "registerPushToken");
const unregisterToken = httpsCallable<{ token: string }, { removed: boolean }>(functions, "unregisterPushToken");
const sendPing = httpsCallable<{ householdId: string; recipientUids: string[]; message: string }, { recipients: number; registeredDevices: number; deliveredDevices: number; failedDevices: number; pushError?: string }>(functions, "sendFamilyPing");
const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
let registeredUid = "";
let signingOut = false;
const deviceOwnerKey = "famdashfun.pushDeviceOwner";
let registrationInFlight: { uid: string; promise: Promise<string> } | null = null;
export type PushTest = { diagnosticId: string; startedAt: number; completedAt?: number; status: "waiting" | "accepted" | "failed" | "cancelled"; errorCounts?: Record<string, number> };
const testPush = httpsCallable<{ token: string; checkOnly: boolean }, { test: PushTest | null }>(functions, "testPushNotification", { timeout: 60000 });

export function pushErrorHelp(code: string) {
  if (/registration-token/.test(code)) return "This device registration expired. FamDashFun will replace it automatically when that phone next opens.";
  if (/credential|authentication|third-party-auth|permission/.test(code)) return "The push service needs its credentials or sending permissions checked. Refreshing phones will not fix that configuration.";
  if (/payload|invalid-argument/.test(code)) return "The push service rejected the request format.";
  return "The push service could not complete the send. Use the diagnostic code to investigate.";
}

export async function testThisDevice(checkOnly = false, onRegistered?: () => void) {
  const token = await ensurePushNotificationsRegistered();
  onRegistered?.();
  return (await testPush({ token, checkOnly })).data.test;
}

function pushOptOutKey() { return `famdashfun.pushOff.${auth.currentUser?.uid || "signed-out"}`; }
function optedOut() { try { return localStorage.getItem(pushOptOutKey()) === "true"; } catch { return false; } }

function notificationApiAvailable() {
  return typeof window !== "undefined" && "Notification" in window;
}

function serviceWorkerAvailable() {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

async function pushPlatformSupported() {
  return notificationApiAvailable() && serviceWorkerAvailable() && await isSupported();
}

async function registerCurrentDevice() {
  if (signingOut) throw new Error("Signing out. Try again after signing in.");
  const uid = auth.currentUser?.uid;
  if (uid && registrationInFlight?.uid === uid) return registrationInFlight.promise;
  const promise = registerCurrentDeviceOnce(uid);
  if (uid) registrationInFlight = { uid, promise };
  try {
    return await promise;
  } finally {
    if (registrationInFlight?.promise === promise) registrationInFlight = null;
  }
}

async function registerCurrentDeviceOnce(uid = auth.currentUser?.uid) {
  if (!uid) throw new Error("Sign in before checking this device.");
  if (!(await pushPlatformSupported())) throw new Error("Push notifications are not supported on this device.");
  if (!vapidKey) throw new Error("Push notifications are not configured for this FamDashFun release.");
  const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  await navigator.serviceWorker.ready;
  const messaging = getMessaging(firebaseApp);
  const options = { serviceWorkerRegistration: registration, vapidKey };
  // Reopening the same account retains its token. An identity change gets a new one.
  let previousOwner = registeredUid;
  try { previousOwner = localStorage.getItem(deviceOwnerKey) || registeredUid; } catch {}
  if (previousOwner !== uid) await deleteToken(messaging);
  try { localStorage.setItem(deviceOwnerKey, uid); } catch {}

  let token = await getToken(messaging, options);
  if (!token) throw new Error("This device did not return a notification token.");
  if (auth.currentUser?.uid !== uid) throw new Error("Account changed. Check this device again.");
  const deviceName = `${navigator.platform || "Web"} · ${navigator.userAgent.includes("Mobile") ? "Mobile" : "Browser"}`;
  let result = (await registerToken({ token, deviceName })).data;
  if (result.rotate) {
    const rejectedToken = token;
    await deleteToken(messaging).catch(() => false);
    token = await getToken(messaging, options);
    if (!token || token === rejectedToken) throw new Error("This device could not refresh its expired alert registration yet. Reopen FamDashFun in a moment.");
    if (auth.currentUser?.uid !== uid) throw new Error("Account changed. Check this device again.");
    result = (await registerToken({ token, deviceName })).data;
    if (result.rotate) throw new Error("This device could not replace its expired alert registration.");
  }
  if (auth.currentUser?.uid !== uid) throw new Error("Account changed. Check this device again.");
  registeredUid = uid;
  return token;
}

export async function currentPushState(): Promise<PushState> {
  if (!(await pushPlatformSupported())) return "unsupported";
  if (window.Notification.permission === "denied") return "blocked";
  if (window.Notification.permission !== "granted") return "off";
  return !optedOut() && vapidKey && registeredUid && registeredUid === auth.currentUser?.uid ? "on" : "off";
}

export async function ensurePushNotificationsRegistered() {
  if (optedOut()) throw new Error("Alerts are turned off for this device. Turn them on before testing.");
  if (!(await pushPlatformSupported()) || window.Notification.permission !== "granted") {
    throw new Error("Push permission is not enabled on this device.");
  }
  return registerCurrentDevice();
}

export async function enablePushNotifications() {
  if (!(await pushPlatformSupported())) throw new Error("Push notifications are not supported on this device.");
  const permission = await window.Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission === "denied" ? "Notifications are blocked in this browser's settings." : "Notification permission was not granted.");
  try { localStorage.removeItem(pushOptOutKey()); } catch { /* Device storage may be disabled. */ }
  return registerCurrentDevice();
}

export async function disablePushNotifications() {
  registeredUid = "";
  try { localStorage.setItem(pushOptOutKey(), "true"); } catch { /* Device storage may be disabled. */ }
  if (!(await pushPlatformSupported())) return;
  const registration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
  const messaging = getMessaging(firebaseApp);
  const token = await getToken(messaging, { ...(registration ? { serviceWorkerRegistration: registration } : {}), ...(vapidKey ? { vapidKey } : {}) }).catch(() => "");
  if (token) await unregisterToken({ token });
  await deleteToken(messaging).catch(() => false);
}

export async function detachPushBeforeSignOut() {
  signingOut = true;
  try {
    await registrationInFlight?.promise.catch(() => undefined);
    if (await pushPlatformSupported() && window.Notification.permission === "granted") {
      const registration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
      const messaging = getMessaging(firebaseApp);
      const token = registration && vapidKey ? await getToken(messaging, { serviceWorkerRegistration: registration, vapidKey }).catch(() => "") : "";
      let removed = false;
      if (token) { try { await unregisterToken({ token }); removed = true; } catch {} }
      const deleted = await deleteToken(messaging).catch(() => false);
      if (!removed && !deleted && token) throw new Error("Could not detach this device's alerts. Please retry sign out.");
    }
    registeredUid = "";
    try { localStorage.removeItem(deviceOwnerKey); } catch {}
  } finally { signingOut = false; }
}

export async function subscribeToForegroundPush(onPayload: (payload: MessagePayload) => void) {
  if (!(await pushPlatformSupported())) return () => undefined;
  return onMessage(getMessaging(firebaseApp), payload => {
    if (payload.data?.type?.startsWith("finance-")) onPayload({ ...payload, notification: undefined, data: { ...payload.data, title: "Finance", body: "Your financial update is ready. Open Finances to view it." } });
    else onPayload(payload);
  });
}

export async function sendFamilyPush(householdId: string, recipientUids: string[], message: string) {
  return (await sendPing({ householdId, recipientUids, message: message.replace(/\s+/g, " ").trim().slice(0, 180) })).data;
}

